/**
 * Phase H10 — Assessment lifecycle status → StatusPill mapping.
 *
 * Pre-H10, every Assessments page had its own inline mapper that
 * collapsed `"draft"` into the StatusPill primitive's amber
 * `"invited"` variant because there was no canonical `"draft"`
 * variant in the primitive. The visual result on the Assessment
 * Templates list was a "Grade 9 — Diagnostic (Draft)" row showing a
 * Status pill literally reading `Invited` — which conflicts with the
 * staff-invite semantic used elsewhere in the IA and confused IAs
 * scanning the table.
 *
 * The fix has two parts:
 *   1. `<StatusPill>` (packages/ui-kit) grew a canonical `"draft"`
 *      variant (slate hue, "Draft" label) so authoring-lifecycle
 *      states have a first-class pill.
 *   2. This module centralises the per-domain mapping so every
 *      Assessment page renders the same pill for the same logical
 *      state — and so a regression test can pin the contract.
 *
 * Both `AssessmentTemplateStatus` and `AssessmentQuestionStatus`
 * have a `"draft"` member; the only domain difference is what the
 * "ready to use" lifecycle name is called (`active` for templates,
 * `approved` for questions). Both map to the same green `active`
 * pill — they convey the same "this is usable now" semantic.
 */
import type { AssessmentQuestionStatus, AssessmentTemplateStatus } from "@eduviaos/shared";
import type { StatusPillStatus } from "@eduviaos/ui-kit";

export function mapTemplateStatusToPill(status: AssessmentTemplateStatus): StatusPillStatus {
  if (status === "active") return "active";
  if (status === "draft") return "draft";
  return "archived";
}

/**
 * Soft-deleted questions (`deleted_at` set) always read as `archived`,
 * regardless of their stored `status`. The bank surfaces them in the
 * "deleted" filter so authors can recover or hard-delete, but the pill
 * must clearly read "Archived" so they aren't mistaken for live items.
 */
export function mapQuestionStatusToPill(
  status: AssessmentQuestionStatus,
  deletedAt?: string | null,
): StatusPillStatus {
  if (deletedAt) return "archived";
  if (status === "approved") return "active";
  if (status === "draft") return "draft";
  return "archived";
}
